import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitAction, clearPreAction } from '../../redux/gameSlice';

const BETTING_STAGES = ['preflop', 'flop', 'turn', 'river'];

// Drives the client-side "pre-action" auto-fire. When the local player has queued a pre-action
// (Check/Fold, Check, or Check/Call Any) while it isn't their turn, this submits the resolved
// action the instant it becomes their turn, then clears the queue. The backend still validates
// every action, so this is purely a convenience.
//
//   check-fold      -> check if nothing to call, else fold
//   check-call-any  -> check if nothing to call, else call whatever's owed
//   check           -> check if still possible; if a bet appeared first it self-clears and the
//                      player acts manually (we never auto-fire an illegal check)
export function usePreActionAutoFire(gameId, game, mySeat) {
    const dispatch = useDispatch();
    const choice = useSelector((state) => state.games.preAction?.[gameId]);
    const firedForTurnRef = useRef(false);

    const me = mySeat != null ? game?.players?.[mySeat] : null;
    const inHand = !!me
        && !me.isFolded
        && !me.isSittingOut
        && game?.awayDeadlines?.[mySeat] == null
        && BETTING_STAGES.includes(game?.currentStage);
    const isMyTurn = inHand && game?.activePlayerPosition === mySeat;
    const timeFrozen = Array.isArray(game?.frozenSeats) && game.frozenSeats.length > 0;
    const owed = me ? Math.max(0, (game?.currentBetLevel || 0) - (me.currentBet || 0)) : 0;
    // A resolved Sacrifice Power-Up blocks this seat from folding this stage (the server
    // rejects the fold) — the fold leg of Check/Fold must never auto-fire while it holds.
    const noFold = !!me?.noFold;

    // Plain "Check" can no longer stand once someone bets — drop it so the player acts manually.
    // Same for Check/Fold whose fold leg would be needed while a Sacrifice fold block holds:
    // firing it would just be rejected, silently eating the queued action.
    useEffect(() => {
        if (!timeFrozen && (choice === 'check' || (choice === 'check-fold' && noFold))
            && owed > 0 && !isMyTurn) {
            dispatch(clearPreAction({ roomId: gameId }));
        }
    }, [choice, owed, noFold, isMyTurn, timeFrozen, gameId, dispatch]);

    // Fire the queued pre-action the moment it becomes our turn (once per turn).
    useEffect(() => {
        if (!isMyTurn) {
            firedForTurnRef.current = false; // reset for the next time our turn comes around
            return;
        }
        // Freeze Time pauses voluntary actions server-side. Keep the queued choice intact and
        // let this same effect fire only after every frozen seat has cleared.
        if (timeFrozen) return;
        if (firedForTurnRef.current || !choice) return;

        let actionType = null;
        // The fold leg never fires under a Sacrifice fold block — resolve to nothing and let
        // the player act manually (the drop effect above usually clears it before this runs).
        if (choice === 'check-fold') actionType = owed > 0 ? (noFold ? null : 'fold') : 'check';
        else if (choice === 'check-call-any') actionType = owed > 0 ? 'call' : 'check';
        else if (choice === 'check') actionType = owed > 0 ? null : 'check';

        firedForTurnRef.current = true;
        dispatch(clearPreAction({ roomId: gameId }));
        if (actionType) dispatch(submitAction({ roomId: gameId, actionType, amount: 0 }));
    }, [isMyTurn, choice, owed, noFold, timeFrozen, gameId, dispatch]);
}
